import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Bookmark,
  Calendar,
  Download,
  GitCompare,
  LineChart,
  Loader2,
  RefreshCw,
  Save,
  Trash2,
} from 'lucide-react';
import { useDashboardBranding } from '../../pages/dashboard/hooks/useDashboardBranding';
import { executiveReportService } from '../../services/executiveReportService';
import {
  buildExecutiveShareContext,
  useReportingSharePublisher,
} from '@saas-foundation/shared-frontend/reporting';

function formatKpiValue(kpi) {
  if (!kpi || kpi.value == null) return kpi?.integrationLabel || '—';
  const v = Number(kpi.value);
  if (Number.isNaN(v)) return String(kpi.value);
  if (kpi.unit === 'currency') return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (kpi.unit === 'percent') return `${v}%`;
  if (kpi.unit === 'days') return `${v}d`;
  return v.toLocaleString();
}

function KpiGrid({ topKpis = [] }) {
  if (!topKpis.length) {
    return <p className="text-sm text-gray-500">No KPI tiles in this report.</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {topKpis.map((kpi) => (
        <div
          key={kpi.id}
          className={`rounded-lg border p-4 ${
            kpi.live && kpi.value != null ? 'border-blue-200 bg-blue-50/40' : 'border-dashed border-gray-300 bg-gray-50'
          }`}
        >
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{kpi.label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatKpiValue(kpi)}</p>
          {kpi.delta != null && (
            <p className="text-xs text-gray-600 mt-1">
              {kpi.delta > 0 ? '+' : ''}
              {kpi.delta} vs prior
            </p>
          )}
          {kpi.sourceLabel && <p className="text-xs text-gray-400 mt-1">{kpi.sourceLabel}</p>}
        </div>
      ))}
    </div>
  );
}

function ScorecardSummary({ scorecards = [] }) {
  if (!scorecards.length) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
      {scorecards.slice(0, 6).map((card) => (
        <div key={card.id} className="rounded-lg border border-gray-200 p-4 bg-white">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">{card.title}</h4>
          <ul className="space-y-1 text-sm text-gray-600">
            {(card.metrics || []).slice(0, 4).map((m) => (
              <li key={m.label} className="flex justify-between gap-2">
                <span>{m.label}</span>
                <span className="font-medium text-gray-900">{m.value ?? '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ReportPreview({ report, title }) {
  if (!report?.payload && !report?.topKpis) return null;
  const payload = report.payload || report;
  return (
    <div className="space-y-4">
      {title && <h3 className="text-lg font-semibold text-gray-900">{title}</h3>}
      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
        <span className="inline-flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          Period: {payload.period || report.report_period || '—'}
        </span>
        <span>Generated: {payload.refreshedAt ? new Date(payload.refreshedAt).toLocaleString() : '—'}</span>
        <span>Status: {payload.dataStatus || report.data_status || '—'}</span>
      </div>
      {payload.branding?.title && (
        <p className="text-sm text-gray-700">
          {payload.branding.title}
          {payload.branding.subtitle ? ` — ${payload.branding.subtitle}` : ''}
        </p>
      )}
      <KpiGrid topKpis={payload.topKpis} />
      <ScorecardSummary scorecards={payload.scorecards} />
      {payload.benchmarks?.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Benchmarks</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2">Metric</th>
                  <th className="px-3 py-2 text-right">Organization</th>
                  <th className="px-3 py-2 text-right">Benchmark</th>
                </tr>
              </thead>
              <tbody>
                {payload.benchmarks.map((b) => (
                  <tr key={b.id} className="border-t border-gray-100">
                    <td className="px-3 py-2">{b.label}</td>
                    <td className="px-3 py-2 text-right">
                      {b.org}
                      {b.unit === '%' ? '%' : b.unit === 'days' ? 'd' : ''}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {b.benchmark}
                      {b.unit === '%' ? '%' : b.unit === 'days' ? 'd' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ExecReportPanel({ activeReport = 'executive-kpi-snapshot' }) {
  const { resolvedOrgName, organizationId, orgContextLoading } = useDashboardBranding({ mode: 'executive' });
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [compareTo, setCompareTo] = useState('prior_month');
  const [draft, setDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [selectedSavedId, setSelectedSavedId] = useState(null);
  const [selectedSavedDoc, setSelectedSavedDoc] = useState(null);
  const [compareBaselineId, setCompareBaselineId] = useState('');
  const [compareTargetId, setCompareTargetId] = useState('');
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const { publishShareContext, clearShareContext } = useReportingSharePublisher();

  const view = activeReport || 'executive-kpi-snapshot';

  const loadSavedList = useCallback(async () => {
    if (!organizationId) return;
    setSavedLoading(true);
    try {
      const items = await executiveReportService.listSavedReports(String(organizationId));
      setSavedReports(items);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to load saved reports' });
    } finally {
      setSavedLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (view === 'saved-reports' || view === 'compare-reports') {
      loadSavedList();
    }
  }, [view, loadSavedList]);

  useEffect(() => {
    if (draft) {
      publishShareContext(
        buildExecutiveShareContext({
          reportName: saveName || `Executive KPI — ${period}`,
          reportPeriod: period,
          draft,
        }),
      );
      return;
    }
    if (selectedSavedDoc) {
      publishShareContext(
        buildExecutiveShareContext({
          reportName: selectedSavedDoc.name,
          reportPeriod: selectedSavedDoc.report_period,
          draft: selectedSavedDoc.payload || selectedSavedDoc,
          executiveReportId: selectedSavedDoc.id,
        }),
      );
      return;
    }
    clearShareContext();
  }, [draft, selectedSavedDoc, saveName, period, publishShareContext, clearShareContext]);

  const handleGenerate = async () => {
    if (!organizationId) return;
    setGenerating(true);
    setMessage(null);
    try {
      const data = await executiveReportService.generateReport({
        organizationId: String(organizationId),
        organizationName: resolvedOrgName,
        period,
        compareTo,
      });
      setDraft(data);
      setSaveName(`Executive KPI — ${period}`);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to generate report' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!organizationId || !draft) return;
    setSaving(true);
    setMessage(null);
    try {
      const saved = await executiveReportService.saveReport(String(organizationId), draft, {
        name: saveName,
        notes: saveNotes,
        period,
      });
      setMessage({ type: 'success', text: `Report "${saved.name}" saved for future comparison.` });
      await loadSavedList();
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to save report' });
    } finally {
      setSaving(false);
    }
  };

  const handleViewSaved = async (reportId) => {
    if (!organizationId) return;
    setSelectedSavedId(reportId);
    try {
      const doc = await executiveReportService.loadSavedReport(String(organizationId), reportId);
      setSelectedSavedDoc(doc);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to load report' });
    }
  };

  const handleDeleteSaved = async (reportId) => {
    if (!organizationId || !window.confirm('Delete this saved report?')) return;
    try {
      await executiveReportService.deleteSavedReport(String(organizationId), reportId);
      if (selectedSavedId === reportId) {
        setSelectedSavedId(null);
        setSelectedSavedDoc(null);
      }
      await loadSavedList();
      setMessage({ type: 'success', text: 'Report deleted.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to delete report' });
    }
  };

  const handleCompare = async () => {
    if (!organizationId || !compareBaselineId || !compareTargetId) return;
    if (compareBaselineId === compareTargetId) {
      setMessage({ type: 'error', text: 'Choose two different reports to compare.' });
      return;
    }
    setComparing(true);
    setMessage(null);
    try {
      const result = await executiveReportService.compareSavedReports(
        String(organizationId),
        compareBaselineId,
        compareTargetId
      );
      setCompareResult(result);
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Comparison failed' });
    } finally {
      setComparing(false);
    }
  };

  const header = useMemo(() => {
    if (view === 'saved-reports') return { title: 'Saved Executive Reports', icon: Bookmark };
    if (view === 'compare-reports') return { title: 'Compare Executive Reports', icon: GitCompare };
    return { title: 'Executive Reports', icon: LineChart };
  }, [view]);

  const HeaderIcon = header.icon;

  if (orgContextLoading) {
    return (
      <div className="flex items-center justify-center flex-1 py-20 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading organization…
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="flex-1 p-8">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
          Select an organization to generate and save executive KPI reports.
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="p-6 space-y-6 max-w-6xl">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <HeaderIcon className="w-7 h-7 text-indigo-600" />
              {header.title}
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Generate snapshots, compare periods, save for reference, and email summaries to stakeholders.
            </p>
          </div>
          {view === 'executive-kpi-snapshot' && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm text-gray-600">
                Period
                <input
                  type="month"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="mt-1 block border border-gray-300 rounded-md px-2 py-1 text-sm"
                />
              </label>
              <label className="text-sm text-gray-600">
                Compare to
                <select
                  value={compareTo}
                  onChange={(e) => setCompareTo(e.target.value)}
                  className="mt-1 block border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  <option value="prior_month">Prior month</option>
                  <option value="prior_year">Same month last year</option>
                </select>
              </label>
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Generate
              </button>
            </div>
          )}
        </div>

        {message && (
          <div
            className={`rounded-lg px-4 py-3 text-sm ${
              message.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-green-50 text-green-800 border border-green-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {view === 'executive-kpi-snapshot' && (
          <>
            {!draft && !generating && (
              <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
                <BarChart3 className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                <p>Click Generate to compose the latest executive KPI report from live RCM data and custom metrics.</p>
              </div>
            )}
            {draft && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-6">
                <ReportPreview report={draft} />
                <div className="border-t border-gray-200 pt-6 space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Save className="w-4 h-4" />
                    Save for later reference
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Report name</label>
                      <input
                        type="text"
                        value={saveName}
                        onChange={(e) => setSaveName(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
                      <input
                        type="text"
                        value={saveNotes}
                        onChange={(e) => setSaveNotes(e.target.value)}
                        placeholder="e.g. Q2 board pack baseline"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving || !saveName.trim()}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save report
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        executiveReportService.downloadReportJson(
                          { ...draft, name: saveName },
                          `${saveName.replace(/\s+/g, '-').toLowerCase()}.json`
                        )
                      }
                      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
                    >
                      <Download className="w-4 h-4" />
                      Download JSON
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {view === 'saved-reports' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Saved reports</h3>
              {savedLoading ? (
                <p className="text-sm text-gray-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                </p>
              ) : savedReports.length === 0 ? (
                <p className="text-sm text-gray-500">No saved reports yet. Generate and save one first.</p>
              ) : (
                <ul className="space-y-2">
                  {savedReports.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => handleViewSaved(r.id)}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm border ${
                          selectedSavedId === r.id
                            ? 'border-indigo-400 bg-indigo-50'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <span className="font-medium text-gray-900 block">{r.name}</span>
                        <span className="text-xs text-gray-500">
                          {r.report_period || '—'} · {new Date(r.created_at).toLocaleDateString()}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6 min-h-[320px]">
              {selectedSavedDoc ? (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{selectedSavedDoc.name}</h3>
                      {selectedSavedDoc.notes && (
                        <p className="text-sm text-gray-600 mt-1">{selectedSavedDoc.notes}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => executiveReportService.downloadReportJson(selectedSavedDoc)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
                        title="Download JSON"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteSaved(selectedSavedDoc.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <ReportPreview report={selectedSavedDoc} />
                </>
              ) : (
                <p className="text-sm text-gray-500">Select a saved report to view its snapshot.</p>
              )}
            </div>
          </div>
        )}

        {view === 'compare-reports' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Select two saved reports</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Baseline (older)</label>
                  <select
                    value={compareBaselineId}
                    onChange={(e) => setCompareBaselineId(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Select report…</option>
                    {savedReports.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.report_period || '—'})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Comparison (newer)</label>
                  <select
                    value={compareTargetId}
                    onChange={(e) => setCompareTargetId(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Select report…</option>
                    {savedReports.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.report_period || '—'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCompare}
                disabled={comparing || !compareBaselineId || !compareTargetId}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
              >
                {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompare className="w-4 h-4" />}
                Compare
              </button>
            </div>

            {compareResult && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <p className="text-sm text-gray-600 mb-4">
                  <strong>{compareResult.baseline?.name}</strong> → <strong>{compareResult.comparison?.name}</strong>
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr>
                        <th className="px-3 py-2">KPI</th>
                        <th className="px-3 py-2 text-right">Baseline</th>
                        <th className="px-3 py-2 text-right">Comparison</th>
                        <th className="px-3 py-2 text-right">Change</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(compareResult.kpi_deltas || []).map((row) => (
                        <tr key={row.id} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-medium text-gray-900">{row.label}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{row.baseline_value ?? '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-900">{row.comparison_value ?? '—'}</td>
                          <td
                            className={`px-3 py-2 text-right font-medium ${
                              row.delta == null
                                ? 'text-gray-400'
                                : row.delta > 0
                                  ? 'text-emerald-600'
                                  : row.delta < 0
                                    ? 'text-red-600'
                                    : 'text-gray-600'
                            }`}
                          >
                            {row.delta == null ? '—' : row.delta > 0 ? `+${row.delta}` : row.delta}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(compareResult.summary_deltas || []).length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-4 h-4" />
                      Monthly summary deltas
                    </h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {compareResult.summary_deltas.map((s) => (
                        <li key={s.key}>
                          {s.key}: {s.baseline_value ?? '—'} → {s.comparison_value ?? '—'}
                          {s.delta != null && (
                            <span className={s.delta >= 0 ? ' text-emerald-600' : ' text-red-600'}>
                              {' '}
                              ({s.delta > 0 ? '+' : ''}
                              {s.delta})
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
